import { useState, type FormEvent } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@atlas/auth";

/**
 * Email verification page
 * - Email and verification code form
 * - confirmSignUp on submit
 * - Resend code button
 */
export function VerifyEmailPage(): React.ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const { confirmSignUp, resendSignUpCode } = useAuth();

  // Get email from navigation state if available
  const initialEmail = (location.state as { email?: string })?.email ?? "";

  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setIsSubmitting(true);

    try {
      await confirmSignUp(email, code);
      // Verification successful - redirect to login
      navigate("/login", {
        state: { message: "Email verified! You can now sign in." },
      });
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to verify email";
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendCode = async (): Promise<void> => {
    if (!email) {
      setError("Please enter your email address");
      return;
    }

    setError(null);
    setSuccess(null);
    setIsResending(true);

    try {
      await resendSignUpCode(email);
      setSuccess("Verification code sent! Check your email.");
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to resend code";
      setError(errorMessage);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg px-4">
      <div className="w-full max-w-sm">
        <h1 className="text-xl font-semibold text-center mb-1.5 text-text-primary">
          Verify your email
        </h1>
        <p className="text-center text-xs text-text-secondary mb-6">
          We sent a verification code to your email. Please enter it below.
        </p>

        {error && (
          <div className="alert-error mb-3">
            {error}
          </div>
        )}

        {success && (
          <div className="alert-success mb-3">
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label
              htmlFor="email"
              className="block mb-1.5 text-xs font-medium text-text-secondary"
            >
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              className="input-field"
            />
          </div>

          <div className="mb-4">
            <label
              htmlFor="code"
              className="block mb-1.5 text-xs font-medium text-text-secondary"
            >
              Verification Code
            </label>
            <input
              id="code"
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              autoComplete="one-time-code"
              placeholder="Enter 6-digit code"
              className="input-field tracking-widest"
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="btn-primary-lg w-full mb-2"
          >
            {isSubmitting ? "Verifying..." : "Verify Email"}
          </button>

          <button
            type="button"
            onClick={handleResendCode}
            disabled={isResending}
            className="btn-secondary w-full"
          >
            {isResending ? "Sending..." : "Resend Code"}
          </button>
        </form>

        <p className="text-center mt-6 text-xs text-text-secondary">
          <Link to="/login" className="link">
            Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
